import { RadioCard } from "components/common/RadioCard";
import { RepaymentDetails } from "components/common/RepaymentDetails";
import { VisibleSection } from "components/common/VisibleSection";
import { ONE, THREE } from "domain/aboutYourLoan";

import { RepaymentOption } from "domain/indicativeRepaymentOptions";
import {
  RepaymentHolidaysType,
  SelectedTreatmentsDetails,
} from "domain/selectedTreatments";
import { useCallback, useEffect } from "react";
import { HideIfNoData } from "util/enhancer";
import { NullableField } from "util/extendedTypes";
import {
  ONE_RH_OPTION_VISIBLE,
  RH_CIRH,
  RH_CRH,
  RH_OPTIONS_HEADING,
} from "./RequestRepaymentHoliday.const";

type JustCRHProps = {
  justCRH: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: () => void;
};

const JustCRHComponent = ({
  justCRH,
  selectedTreatments,
  onChange,
}: JustCRHProps) => {
  const isSelected =
    justCRH.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  return (
    <>
      <RadioCard
        name="crhOptions"
        isSelected={isSelected}
        value={justCRH.optionType}
        onChange={onChange}
        label={RH_CRH}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={justCRH}
          />
        )}
      </RadioCard>
    </>
  );
};

const JustCRH = HideIfNoData<
  JustCRHProps,
  NullableField<JustCRHProps, "justCRH">
>((p) => !p.justCRH)(JustCRHComponent);

type JustCIRHProps = {
  justCIRH: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: () => void;
};

const JustCIRHComponent = ({
  justCIRH,
  selectedTreatments,
  onChange,
}: JustCIRHProps) => {
  const isSelected =
    justCIRH.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  return (
    <>
      <RadioCard
        name="cirhOptions"
        isSelected={isSelected}
        value={justCIRH.optionType}
        onChange={onChange}
        label={RH_CIRH}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={justCIRH}
          />
        )}
      </RadioCard>
    </>
  );
};

const JustCIRH = HideIfNoData<
  JustCIRHProps,
  NullableField<JustCIRHProps, "justCIRH">
>((p) => !p.justCIRH)(JustCIRHComponent);

type RHExtendTermActiveOptionsProps = {
  CRHTaken: number;
  CIRHTaken: number;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
  CRH: RepaymentOption;
  CIRH: RepaymentOption;
};

const RHExtendTermActiveOptionsComponent = ({
  CRHTaken,
  CIRHTaken,
  selectedTreatments,
  onChange,
  CRH,
  CIRH,
}: RHExtendTermActiveOptionsProps) => {
  const justCRH = (CRHTaken < THREE && CRH) || null;
  const justCIRH = (CIRHTaken < ONE && CIRH) || null;
  const RHVisible = CRHTaken < THREE && CIRHTaken < ONE;
  const handleChange = useCallback(
    (option: RepaymentOption) => {
      let selectedRHType =
        option === CRH
          ? RepaymentHolidaysType.CapitalRepaymentHoliday
          : RepaymentHolidaysType.CapitalIntersestRepaymentHoliday;
      onChange({
        ...selectedTreatments,
        selectedRHType: selectedRHType,
        selectedOptionType: option.optionType,
        repaymentOption: option,
      });
    },
    [onChange, selectedTreatments, CRH, CIRH]
  );

  useEffect(() => {
    if (!RHVisible) {
      justCRH && handleChange(CRH);
      justCIRH && handleChange(CIRH);
    }
  }, [justCRH, justCIRH, CRH, CIRH, RHVisible]);

  return (
    <div className="selected-options section-margin">
      <VisibleSection visible={RHVisible}>
        <label id="cirh-repaymentOptions" className="title">
          {RH_OPTIONS_HEADING}
        </label>
      </VisibleSection>
      <VisibleSection visible={!RHVisible}>
        <label id="cirh-repaymentOptions" className="title">
          {ONE_RH_OPTION_VISIBLE}
        </label>
      </VisibleSection>
      <fieldset aria-labelledby="repaymentOptions">
        <JustCRH
          justCRH={justCRH}
          selectedTreatments={selectedTreatments}
          onChange={() => handleChange(CRH)}
        />
        <JustCIRH
          justCIRH={justCIRH}
          selectedTreatments={selectedTreatments}
          onChange={() => handleChange(CIRH)}
        />
      </fieldset>
    </div>
  );
};

export const RHExtendTermActiveOptions = HideIfNoData<
  RHExtendTermActiveOptionsProps,
  NullableField<RHExtendTermActiveOptionsProps, "CRH" | "CIRH">
>((p) => !p.CRH && !p.CIRH)(RHExtendTermActiveOptionsComponent);
